<template>
  <div
    class="fixed top-0 left-0 w-full h-full z-[12345678900] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4"
    @click="handleCancel"
  >
    <div
      class="relative w-full max-w-md bg-white rounded-[2rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-white flex flex-col animate-in zoom-in duration-200"
      @click.stop
    >
      <div class="px-4 pt-4 pb-4 flex items-center justify-between">
        <div class="flex items-center gap-4">
          <div
            class="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-500 shadow-sm"
          >
            <UserPlusIcon class="w-6 h-6" />
          </div>
          <h2 class="text-xl font-bold text-[#2d3748] tracking-tight">
            Add Player
          </h2>
        </div>
        <button
          class="p-2 rounded-full hover:bg-slate-50 text-slate-300 hover:text-slate-500 transition-all"
          @click="handleCancel"
        >
          <XMarkIcon class="w-6 h-6" />
        </button>
      </div>

      <div class="px-4 pb-8 space-y-6">
        <div v-if="!isUploadingNewPlayer" class="space-y-1">
          <label
            class="text-[11px] font-semibold text-slate-600 uppercase tracking-widest px-1"
          >
            Freebet Campaign
          </label>

          <span
            class="font-bold text-[#2d3748] text-lg w-full block overflow-hidden truncate text-left"
            >{{ detail?.title || "testdavtestdav" }}</span
          >
        </div>

        <div v-if="!isUploadingNewPlayer" class="space-y-1">
          <div class="flex items-center gap-2 px-1">
            <UserIcon class="w-4 h-4 text-slate-600" />
            <label
              class="text-[11px] font-semibold text-slate-600 uppercase tracking-widest"
              for="player-id"
            >
              {{ $t("addPlayerToFreebet.playerId") }}
            </label>
          </div>

          <p class="text-[11px] text-slate-600 italic px-1">
            Enter the Player ID to assign
          </p>

          <input
            type="text"
            id="player-id"
            v-model="formData.player_id"
            placeholder="Enter Player ID"
            class="w-full px-3 py-3 mt-2 bg-white border border-slate-200 rounded-2xl outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/5 transition-all font-medium text-slate-700 placeholder:text-slate-300"
            @input="formData.player_id = formData.player_id.replace(/\s/g, '')"
            @keydown.space.prevent
          />
        </div>

        <div
          v-if="isUploadingNewPlayer"
          class="py-10 flex flex-col items-center justify-center"
        >
          <div
            class="w-12 h-12 border-3 border-emerald-100 border-t-emerald-500 rounded-full animate-spin"
          ></div>
          <p
            class="text-xs font-bold text-slate-600 mt-4 uppercase tracking-widest"
          >
            Assigning Player...
          </p>
        </div>

        <div v-if="!isUploadingNewPlayer" class="flex gap-4 pt-2">
          <button
            class="flex-1 px-3 py-3 rounded-2xl font-bold text-slate-500 bg-[#edf2f7] hover:bg-[#e2e8f0] transition-all active:scale-95"
            @click="handleCancel"
          >
            {{ $t("util.cancel") }}
          </button>

          <button
            class="flex-[1.5] px-3 py-3 rounded-2xl font-bold text-white bg-[#cbd5e0] disabled:opacity-50 transition-all active:scale-95 flex items-center justify-center gap-2 shadow-sm"
            :class="{
              '!bg-emerald-500 shadow-emerald-100 hover:bg-emerald-500 shadow-lg':
                formData.player_id.trim(),
            }"
            @click="handleCreate"
            :disabled="!formData.player_id.trim()"
          >
            <UserPlusIcon class="w-5 h-5" />
            Add Player
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.animate-in {
  animation-duration: 200ms;
}
.fade-in {
  animation-name: fadeIn;
}
.zoom-in {
  animation-name: zoomIn;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>

<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import { postFetch } from "@/api/axiosClient";
import {
  XMarkIcon,
  InformationCircleIcon,
  UserIcon,
  UserPlusIcon,
} from "@heroicons/vue/24/outline";

export default {
  name: "AddPlayerToCampaignRestricted",
  props: {
    detail: { type: Object },
  },
  components: {
    XMarkIcon,
    InformationCircleIcon,
    UserIcon,
    UserPlusIcon,
  },
  data() {
    return {
      formData: {
        player_id: "",
      },
      isUploadingNewPlayer: false,
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen"]),
  },
  mounted() {
    document.body.style.overflow = "hidden";
    // Nota: Ho mantenuto l'import ma con Tailwind lo stile SCSS potrebbe non servire più
    import("@/assets/styles/components/swal2ModalDialog.scss");
  },
  beforeUnmount() {
    document.body.style.overflow = "auto";
  },
  methods: {
    resetForm() {
      this.formData.player_id = "";
    },
    async handleCreate() {
      if (!this.formData.player_id.trim()) return;
      this.isUploadingNewPlayer = true;

      try {
        await postFetch(`campaigns/restricted/${this.detail?._id}`, {
          player_id: this.formData.player_id,
        });

        await Swal.fire({
          title: "Success!",
          text: "Player added successfully",
          icon: "success",
          confirmButtonColor: "#4f46e5",
          customClass: {
            container: "z-[100000]",
          },
        });

        window.location.reload();
      } catch (error) {
        console.error("Errore durante l'aggiornamento:", error);
        await Swal.fire({
          title: "Error",
          text: "Failed to add the player to freebet.",
          icon: "error",
          confirmButtonColor: "#ef4444",
        });
      } finally {
        this.isUploadingNewPlayer = false;
      }
    },
    handleCancel() {
      this.$emit("add-player-modal-open");
      this.resetForm();
    },
  },
};
</script>
